import Color from "color";
import type { ReadableTheme, Theme } from "@/lib/theme/type";

/**
 * Contrast measurement and the derived "readable" text tone for the semantic ramps.
 *
 * The semantic ramps (`error`, `warning`, `success`) have the steps `10 / 20 / 80 / 100`, and `100`
 * is the only dark step. Every coloured label therefore ends up on `100` — a step that was picked
 * to fill surfaces and draw icons, not to be read. On its own tint that lands around 2–3.6:1 where
 * WCAG AA asks for 4.5:1.
 *
 * A fixed extra hex would only have fixed Boomer: `ThemeConfig.config` is free-form JSON, so every
 * white-label brand brings its own `100` and its own `10` with the same ramp shape. Instead we
 * derive a `120` step from whatever the brand already supplies — the same colour, stepped towards
 * black (or towards white on a dark theme) until it clears the norm on the backgrounds it actually
 * sits on. The tint stays recognisable, the contrast is guaranteed, and no brand theme has to be
 * touched.
 */

/** WCAG 2.1 AA, body text. */
export const AA_NORMAL_TEXT = 4.5;
/** WCAG 2.1 AA, large text (>=18.66px bold or >=24px) and meaningful non-text graphics. */
export const AA_LARGE_TEXT = 3;

export type SemanticName = "error" | "warning" | "success";

export const SEMANTIC_NAMES = [
  "error",
  "warning",
  "success",
] as const satisfies readonly SemanticName[];

/**
 * The alpha the status chips ask for (`bg-error-100/10`). A label on such a chip is not sitting on
 * the `10` step — it sits on `100` composited over the surface behind it, which is a different and
 * usually darker colour.
 *
 * Note that those chips are currently _not_ tinted at all: Tailwind cannot apply an opacity
 * modifier to a bare `var(--color-…)` value, so `bg-error-100/10` compiles to nothing and the chip
 * renders on the plain surface. We still derive against the composited tint, because it is the
 * darker background and therefore the safe one — this way the labels stay readable if the tints are
 * ever repaired. See the PR for #99.
 */
export const SEMANTIC_TINT_ALPHA = 0.1;

/**
 * The API returns `ThemeConfig.config` as free-form JSON and the provider does not deep-merge, so a
 * ramp step can be missing, empty, or something that is not a colour at all. Everything in here
 * runs on the boot path for every brand, so nothing may throw: an unparseable value is simply not a
 * background we can reason about.
 */
function parse(color: unknown): Color | undefined {
  if (typeof color !== "string" || color.trim() === "") {
    return undefined;
  }

  try {
    return Color(color);
  } catch {
    return undefined;
  }
}

function channels(color: string): [number, number, number] {
  const [r, g, b] = (parse(color) ?? Color("#000000")).rgb().array();
  return [r, g, b];
}

/** WCAG 2.1 relative luminance of an opaque colour. */
export function relativeLuminance(color: string): number {
  const [r, g, b] = channels(color).map((channel) => {
    const srgb = channel / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.1 contrast ratio between two opaque colours. Ranges from 1 (identical) to 21 (b/w). */
export function contrastRatio(a: string, b: string): number {
  const luminanceA = relativeLuminance(a);
  const luminanceB = relativeLuminance(b);
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);

  return (lighter + 0.05) / (darker + 0.05);
}

/** Flatten a translucent colour onto an opaque background, the way the compositor would. */
export function composite(foreground: string, background: string, alpha: number): string {
  const front = channels(foreground);
  const back = channels(background);

  return Color.rgb(front.map((channel, index) => channel * alpha + back[index] * (1 - alpha)))
    .hex()
    .toLowerCase();
}

/** How many 1% steps we are willing to walk towards black/white before giving up. */
const TONE_STEPS = 100;

/**
 * Step `base` towards black or towards white until it clears `target` against _every_ background it
 * has to survive. Direction is decided by the backgrounds themselves, so a dark theme lightens
 * where a light theme darkens.
 *
 * If even the endpoint cannot clear the norm — a background sitting in the mid greys has no
 * readable tone in any hue — the endpoint with the best contrast is returned. Call
 * {@link isReadableAgainst} if you need to know whether that happened.
 */
export function readableTone(
  base: string,
  backgrounds: readonly string[],
  target: number = AA_NORMAL_TEXT,
): string {
  const normalised = Color(base).hex().toLowerCase();

  if (backgrounds.length === 0) {
    return normalised;
  }

  const clears = (candidate: string) =>
    backgrounds.every((background) => contrastRatio(candidate, background) >= target);

  if (clears(normalised)) {
    return normalised;
  }

  const meanLuminance =
    backgrounds.reduce((total, background) => total + relativeLuminance(background), 0) /
    backgrounds.length;
  const endpoint = meanLuminance > 0.5 ? "#000000" : "#ffffff";

  for (let step = 1; step <= TONE_STEPS; step++) {
    const candidate = Color(normalised)
      .mix(Color(endpoint), step / TONE_STEPS)
      .hex()
      .toLowerCase();

    if (clears(candidate)) {
      return candidate;
    }
  }

  return endpoint;
}

/** Whether `color` clears `target` against every one of `backgrounds`. */
export function isReadableAgainst(
  color: string,
  backgrounds: readonly string[],
  target: number = AA_NORMAL_TEXT,
): boolean {
  return backgrounds.every((background) => contrastRatio(color, background) >= target);
}

/**
 * The backgrounds a semantic _label_ actually sits on, darkest first. Two shapes exist in the app:
 * the `10` step used as a solid tint, and `100` washed over the surface at
 * {@link SEMANTIC_TINT_ALPHA}. A tone has to be readable on both, plus on the plain page surface.
 */
export function semanticTextBackgrounds(theme: Theme, name: SemanticName): string[] {
  const ramp = theme.color?.[name];
  const surface = theme.color?.surface;
  const hundred = parse(ramp?.[100])?.hex();

  const washes = hundred
    ? [surface?.modal, surface?.background]
        .map((background) => parse(background)?.hex())
        .filter((background): background is string => background !== undefined)
        .map((background) => composite(hundred, background, SEMANTIC_TINT_ALPHA))
    : [];

  return [ramp?.[10], ...washes, surface?.background, surface?.primary]
    .map((background) => parse(background)?.hex().toLowerCase())
    .filter((background): background is string => background !== undefined);
}

/**
 * The `120` step: the tone the app uses to render semantic _text_. Derived from the `100` the brand
 * already supplies, so a brand that has never been seen still gets a readable label.
 */
export function semanticTextTone(theme: Theme, name: SemanticName): string | undefined {
  const base = parse(theme.color?.[name]?.[100]);

  if (!base) {
    return undefined;
  }

  return readableTone(base.hex(), semanticTextBackgrounds(theme, name));
}

/** The derived `120` step per semantic ramp, ready to be published as CSS variables. */
export function deriveReadableTones(theme: Theme): Partial<Record<SemanticName, string>> {
  return SEMANTIC_NAMES.reduce<Partial<Record<SemanticName, string>>>((tones, name) => {
    const tone = semanticTextTone(theme, name);

    if (tone) {
      tones[name] = tone;
    }

    return tones;
  }, {});
}

/**
 * The theme the app actually paints with: the brand's own values plus the derived `120` step per
 * semantic ramp. A ramp the brand did not supply is left alone, so `convertThemeToCssVars` skips it
 * and Tailwind's precomputed `var(--color-x-120, …)` fallback paints instead.
 */
export function withReadableTones(theme: Theme): ReadableTheme {
  const tones = deriveReadableTones(theme);

  return {
    ...theme,
    color: SEMANTIC_NAMES.reduce(
      (color, name) => {
        const tone = tones[name];
        return tone ? { ...color, [name]: { ...color[name], 120: tone } } : color;
      },
      { ...theme.color },
    ),
  } as ReadableTheme;
}
